import { useCallback, useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { clean } from 'redux-saga-thunk';
import {
  resourceCreateRequest,
  resourceListReadRequest,
  resourceListReadSuccess,
} from 'store/resource/actions';
import * as resourceSelectors from 'store/resource/selectors';

const apis = {
  submitTimeReport: 'submitTimeReport',
  rateWorkAssignment: 'rateWorkAssignment',
  updateWorkAssignmentExpenses: 'updateWorkAssignmentExpenses',
  searchSingleWorkAssignment: 'searchSingleWorkAssignment',
};

const WORK_ASSIGNMENT_RESOURCE_KEY = 'WorkAssignments';

const getResourceList = (state, resource) =>
  resourceSelectors.getList(state.resource, resource) || [];

const getUpdatedAssignment = state =>
  getResourceList(state, apis.searchSingleWorkAssignment)?.[0] || null;

const useAssignmentTimeReportSubmissionFlow = (workAssignment = {}) => {
  const dispatch = useDispatch();
  const store = useStore();

  const cleanRequestState = useCallback(() => {
    dispatch(clean(`${apis.submitTimeReport}Create`));
    dispatch(clean(`${apis.rateWorkAssignment}Create`));
    dispatch(clean(`${apis.updateWorkAssignmentExpenses}Create`));
    dispatch(clean(`${apis.searchSingleWorkAssignment}ListRead`));
  }, [dispatch]);

  const syncUpdatedAssignmentToResource = useCallback(
    (resource, refreshedAssignment) => {
      const currentList = getResourceList(store.getState(), resource);
      const hasMatchingAssignment = currentList.some(
        item =>
          item?.WorkAssignmentIdentifier ===
          refreshedAssignment?.WorkAssignmentIdentifier,
      );

      if (!hasMatchingAssignment) {
        return;
      }

      const nextList = currentList.map(item =>
        item?.WorkAssignmentIdentifier ===
        refreshedAssignment.WorkAssignmentIdentifier
          ? refreshedAssignment
          : item,
      );

      dispatch(
        resourceListReadSuccess(
          resource,
          WORK_ASSIGNMENT_RESOURCE_KEY,
          { WorkAssignments: nextList },
          null,
          null,
          true,
        ),
      );
    },
    [dispatch, store],
  );

  const refreshSingleWorkAssignment = useCallback(async () => {
    if (!workAssignment.WorkAssignmentIdentifier) {
      return null;
    }

    try {
      await dispatch(
        resourceListReadRequest(
          apis.searchSingleWorkAssignment,
          WORK_ASSIGNMENT_RESOURCE_KEY,
          {
            WorkAssignmentIdentifier: workAssignment.WorkAssignmentIdentifier,
          },
          true,
        ),
      );

      const refreshedAssignment = getUpdatedAssignment(store.getState());

      if (!refreshedAssignment) {
        return null;
      }

      syncUpdatedAssignmentToResource('calendarAssignment', refreshedAssignment);
      syncUpdatedAssignmentToResource('assignmentList', refreshedAssignment);

      return refreshedAssignment;
    } catch (error) {
      return null;
    }
  }, [
    dispatch,
    store,
    syncUpdatedAssignmentToResource,
    workAssignment.WorkAssignmentIdentifier,
  ]);

  const submitTimeReportFlow = useCallback(
    async ({ expenses, hasExpenses, rate, shouldSendRating, timeReport }) => {
      cleanRequestState();

      try {
        if (hasExpenses) {
          await dispatch(
            resourceCreateRequest(apis.updateWorkAssignmentExpenses, expenses),
          );
        }

        await dispatch(
          resourceCreateRequest(apis.submitTimeReport, timeReport),
        );

        if (shouldSendRating) {
          try {
            await dispatch(
              resourceCreateRequest(apis.rateWorkAssignment, rate),
            );
          } catch (error) {
            // Rating is non-blocking for the main submission flow.
          }
        }

        await refreshSingleWorkAssignment();
      } finally {
        cleanRequestState();
      }
    },
    [cleanRequestState, dispatch, refreshSingleWorkAssignment],
  );

  useEffect(() => {
    return () => {
      cleanRequestState();
    };
  }, [cleanRequestState]);

  return {
    submitTimeReportFlow,
  };
};

export default useAssignmentTimeReportSubmissionFlow;
